'use client';

import { ReactNode } from 'react';
import React from 'react';

export interface Feature {
  icon: ReactNode;
  text: string;
}

export interface FeaturesProps {
  /**
   * The features to display
   */
  features: Feature[];
  /**
   * The title to display above the features
   */
  title?: string;
  /**
   * The number of columns to display on desktop
   * @default 2
   */
  columns?: 1 | 2 | 3 | 4;
  /**
   * The color scheme to use for the features
   * @default "primary"
   */
  colorScheme?: 'primary' | 'blue' | 'purple' | 'green' | 'orange' | 'red' | 'supernova';
}

/**
 * A component to display a grid of features with icons and text
 */
export function Features({
  features,
  title = "Key Features",
  columns = 2,
  colorScheme = 'primary'
}: FeaturesProps) {
  if (!features || features.length === 0) {
    return null;
  }

  // Map color scheme to tailwind classes
  const getColorClasses = () => {
    switch (colorScheme) {
      case 'blue':
        return {
          icon: 'text-blue-500 dark:text-blue-400',
          iconBg: 'bg-blue-50 group-hover:bg-blue-100 dark:bg-blue-950/50 dark:group-hover:bg-blue-900/50',
          hover: 'hover:border-blue-200 hover:bg-blue-50/50 dark:hover:border-blue-800 dark:hover:bg-blue-950/30'
        };
      case 'purple':
        return {
          icon: 'text-purple-500 dark:text-purple-400',
          iconBg: 'bg-purple-50 group-hover:bg-purple-100 dark:bg-purple-950/50 dark:group-hover:bg-purple-900/50',
          hover: 'hover:border-purple-200 hover:bg-purple-50/50 dark:hover:border-purple-800 dark:hover:bg-purple-950/30'
        };
      case 'green':
        return {
          icon: 'text-green-500 dark:text-green-400',
          iconBg: 'bg-green-50 group-hover:bg-green-100 dark:bg-green-950/50 dark:group-hover:bg-green-900/50',
          hover: 'hover:border-green-200 hover:bg-green-50/50 dark:hover:border-green-800 dark:hover:bg-green-950/30'
        };
      case 'orange':
        return {
          icon: 'text-orange-500 dark:text-orange-400',
          iconBg: 'bg-orange-50 group-hover:bg-orange-100 dark:bg-orange-950/50 dark:group-hover:bg-orange-900/50',
          hover: 'hover:border-orange-200 hover:bg-orange-50/50 dark:hover:border-orange-800 dark:hover:bg-orange-950/30'
        };
      case 'red':
        return {
          icon: 'text-red-500 dark:text-red-400',
          iconBg: 'bg-red-50 group-hover:bg-red-100 dark:bg-red-950/50 dark:group-hover:bg-red-900/50',
          hover: 'hover:border-red-200 hover:bg-red-50/50 dark:hover:border-red-800 dark:hover:bg-red-950/30'
        };
      case 'supernova':
        return {
          icon: 'text-[#FF6B00] dark:text-[#FF8533]',
          iconBg: 'bg-[#FFF0E5] group-hover:bg-[#FFE0CC] dark:bg-[#3D2A1A] dark:group-hover:bg-[#4D3520]',
          hover: 'hover:border-[#FFCFB0] hover:bg-[#FFF8F2] dark:hover:border-[#6B4730] dark:hover:bg-[#2D1F13]'
        };
      case 'primary':
      default:
        return {
          icon: 'text-primary dark:text-primary',
          iconBg: 'bg-primary/10 group-hover:bg-primary/20 dark:bg-primary/20 dark:group-hover:bg-primary/30',
          hover: 'hover:border-primary/40 hover:bg-primary/5 dark:hover:border-primary/30 dark:hover:bg-primary/10'
        };
    }
  };

  const colorClasses = getColorClasses();
  
  // Map columns to grid classes
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  }[columns];

  return (
    <div className="mt-12 mb-8">
      {title && <h2 className="text-2xl font-bold mb-6 text-foreground">{title}</h2>}
      <div className={`grid ${gridCols} gap-6`}>
        {features.map((feature, index) => (
          <div 
            key={index} 
            className={`flex items-start gap-4 p-5 rounded-lg border border-border/60 ${colorClasses.hover} transition-colors group`}
          >
            <div className={`${colorClasses.icon} p-2.5 ${colorClasses.iconBg} rounded-md transition-colors`}>
              {feature.icon}
            </div>
            <div>
              <div className="font-medium text-foreground mb-1 group-hover:text-foreground">
                {feature.text.split(' - ')[0] || feature.text}
              </div>
              {feature.text.includes(' - ') && (
                <div className="text-sm text-muted-foreground group-hover:text-muted-foreground">
                  {feature.text.split(' - ')[1]}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
} 